import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@vimaana/ui";
import type {
  Beleg,
  BelegListe,
  BelegPauschalenAntwort,
  BelegPatch,
  Dublette,
  PassendeBankzeile,
  ProjektOption,
  Lieferant,
} from "../lib/belege";
import { finanzenNachziehen, finanzenNachziehenOhneBelegliste } from "./finanzenNachziehen";

/**
 * Belege (Backend: hetzner/webhook/kontor.py, delegiert an scanflow.py).
 *
 * Kontor liest und schreibt dieselbe Tabelle wie ScanFlow und die Desktop-App —
 * es gibt bewusst keinen zweiten Schreibweg, nur eigene Routen im
 * Kontor-Session-Scope.
 */

const belegeKey = ["belege"] as const;

/** Groesse einer Seite beim Nachladen. Serverseitige Obergrenze ist 500. */
const SEITE = 500;

/**
 * Alle Belege — serverseitig geblaettert, im Client zu einer Liste vereint.
 *
 * Vorher stand hier ein festes `?limit=300`. Damit hing nicht nur die Liste bei
 * 300 Zeilen fest, sondern auch der Zaehler in der Filterleiste — er zaehlte die
 * geladenen Zeilen, nicht die vorhandenen. Sobald der Bestand ueber 300 lag,
 * zeigte „Alle" dauerhaft 300 an, und aeltere Belege waren ueber die Suche gar
 * nicht mehr erreichbar. `total` kommt aus einem COUNT(*) des Servers und ist
 * unabhaengig davon, wie viel geladen wurde.
 */
export function useBelege() {
  return useQuery<BelegListe>({
    queryKey: [...belegeKey, "liste"],
    queryFn: async () => {
      const erste = await apiGet<BelegListe>(`/api/kontor/belege?limit=${SEITE}&offset=0`);
      const items = [...erste.items];
      // Die Filter-Chips zaehlen ueber den gesamten Bestand und die Suche geht
      // ueber alle Zeilen — beides braucht die vollstaendige Liste. Geblaettert
      // wird trotzdem, weil eine einzelne Antwort mit ein paar tausend Belegen
      // am Proxy-Timeout stirbt.
      while (items.length < erste.total) {
        const weiter = await apiGet<BelegListe>(
          `/api/kontor/belege?limit=${SEITE}&offset=${items.length}`,
        );
        if (!weiter.items.length) break; // Schutz gegen Endlosschleife
        items.push(...weiter.items);
      }
      return { total: erste.total, items };
    },
    // Groß (~430 Belege) und nur durch Belegänderungen veraltet, die die Liste
    // selbst nachführen (useBelegAendern/useBelegUebernehmen). Kein Neuladen
    // bei jedem Wechsel zurück ins Fenster (global an, hier teuer).
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Zuletzt an Belegen genutzte Projekte — Vorschlaege im Projekt-Picker. */
export function useZuletztGenutzteProjekte(aktiv: boolean) {
  return useQuery<ProjektOption[]>({
    queryKey: [...belegeKey, "zuletzt-projekte"],
    queryFn: () => apiGet<ProjektOption[]>("/api/kontor/belege/recent-projekte?limit=8"),
    enabled: aktiv,
    staleTime: 60_000,
  });
}

/**
 * Moegliche Dubletten zu einem Beleg.
 *
 * Die Pruefung gab es serverseitig laengst, im Kontor war sie nur nie
 * angeschlossen — deshalb landete eine Rechnung, die zweimal ins Postfach kam,
 * unbemerkt zweimal in der Buchhaltung.
 */
export function useDubletten(belegId?: string) {
  return useQuery<Dublette[]>({
    queryKey: [...belegeKey, "dubletten", belegId],
    queryFn: () =>
      apiGet<Dublette[]>(`/api/kontor/belege/${encodeURIComponent(belegId ?? "")}/duplicates`),
    enabled: !!belegId,
    staleTime: 30_000,
  });
}

/** Seitenzahl des Belegs — wie viele Bilder die Vorschau anfordern muss. */
export function useBelegSeiten(belegId?: string) {
  return useQuery<{ seiten: number; ist_pdf: boolean }>({
    queryKey: [...belegeKey, "seiten", belegId],
    queryFn: () =>
      apiGet<{ seiten: number; ist_pdf: boolean }>(
        `/api/kontor/belege/${encodeURIComponent(belegId ?? "")}/seiten`,
      ),
    enabled: !!belegId,
    staleTime: 600_000,
  });
}

/**
 * Aufteilung eines Belegs auf mehrere Projekte setzen (autoritativ).
 *
 * Der Server prueft, dass die Summe dem Beleg-Netto entspricht, und legt je
 * Anteil eine eigene Projektausgabe an. Eine leere oder einzeilige Liste hebt
 * die Aufteilung wieder auf.
 */
export function useAufteilungSetzen() {
  const aktualisieren = useBelegeAktualisieren();
  return useMutation({
    mutationFn: (v: { belegId: string; zeilen: { projekt_id: string; netto: number }[] }) =>
      apiPut<Beleg>(`/api/kontor/belege/${encodeURIComponent(v.belegId)}/aufteilung`, {
        zeilen: v.zeilen,
      }),
    onSuccess: aktualisieren,
  });
}

function useBelegeAktualisieren() {
  const qc = useQueryClient();
  // Jede Belegaenderung kann Konto, Ausgangskorb, Lohnkonto, Projekt und das
  // Lieferantenblatt verschieben — eine Liste fuer beide Reiter (25.09.2026).
  return () => finanzenNachziehen(qc);
}

/**
 * Einen Beleg neu auswerten — Betraege, Lieferant, IBAN.
 *
 * Bewusst je Beleg einzeln und nicht als Stapel: ein Vision-Aufruf dauert
 * Sekunden, und wer zehn frisch abgerufene Belege in einen Request packt,
 * bekommt statt eines Ergebnisses einen Timeout. Einzeln laesst sich der
 * Fortschritt ausserdem zeigen.
 */
export function useBelegAuswerten() {
  const aktualisieren = useBelegeAktualisieren();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<Beleg>(`/api/kontor/belege/${encodeURIComponent(id)}/extract`, {}),
    onSuccess: aktualisieren,
  });
}

/** Laufende Änderungen je Beleg — erst wenn die letzte zurück ist, gilt die Serverantwort. */
const offeneAenderungen = new Map<string, number>();

/**
 * Einen Beleg ändern.
 *
 * Bis 25.09.2026 („sehr hakelig"): jede Änderung sperrte alle Felder, bis
 * PATCH UND das Neuladen der ganzen Liste (~1 s) durch waren. Wer nach dem
 * Brutto mit Tab ins MwSt-Feld sprang, landete in einem gesperrten Feld — der
 * Fokus fiel heraus, die Eingabe ging ins Leere. Jetzt: die Änderung steht
 * sofort in der Liste (optimistisch), Felder bleiben frei, mehrere Änderungen
 * dürfen gleichzeitig laufen. Die Antwort des Servers übernimmt erst die
 * letzte, sonst überschriebe eine ältere Antwort eine neuere Eingabe; danach
 * wird alles Abhängige nachgeladen (bei Fehlern auch — das nimmt die
 * optimistische Änderung zurück).
 */
export function useBelegAendern(opts?: { onFehler?: (e: unknown) => void; onGespeichert?: () => void }) {
  const qc = useQueryClient();
  const aktualisieren = useBelegeAktualisieren();
  const listeKey = [...belegeKey, "liste"];
  const ersetzen = (id: string, neu: (b: Beleg) => Beleg) =>
    qc.setQueryData<BelegListe>(listeKey, (alt) =>
      alt ? { ...alt, items: alt.items.map((b) => (b.id === id ? neu(b) : b)) } : alt,
    );
  return useMutation({
    mutationFn: (v: { id: string; patch: BelegPatch }) =>
      apiPatch<Beleg>(`/api/kontor/belege/${encodeURIComponent(v.id)}`, v.patch),
    onMutate: async (v) => {
      offeneAenderungen.set(v.id, (offeneAenderungen.get(v.id) ?? 0) + 1);
      await qc.cancelQueries({ queryKey: listeKey });
      ersetzen(v.id, (b) => ({ ...b, ...(v.patch as Partial<Beleg>) }));
    },
    onError: (e) => opts?.onFehler?.(e),
    onSettled: (neu, fehler, v) => {
      const rest = (offeneAenderungen.get(v.id) ?? 1) - 1;
      if (rest > 0) { offeneAenderungen.set(v.id, rest); return; }
      offeneAenderungen.delete(v.id);
      if (neu && !fehler) {
        ersetzen(v.id, (b) => ({ ...b, ...neu }));
        opts?.onGespeichert?.();
        finanzenNachziehenOhneBelegliste(qc);
      } else {
        // Fehler: die optimistische Änderung zurücknehmen — hier lohnt die ganze Liste.
        aktualisieren();
      }
    },
  });
}

/** Entwurf pruefen und uebernehmen: entwurf → erfasst, danach verbuchen. */
/**
 * Entwurf übernehmen — sofort sichtbar: der Beleg verlässt „Entwurf" in der
 * Liste, bevor der Server fertig ist (der braucht ~1,5 s: Datei umbenennen,
 * Projektkosten buchen, an DATEV senden). Die Antwort setzt den echten Stand.
 */
export function useBelegUebernehmen() {
  const qc = useQueryClient();
  const aktualisieren = useBelegeAktualisieren();
  const listeKey = [...belegeKey, "liste"];
  const ersetzen = (id: string, neu: (b: Beleg) => Beleg) =>
    qc.setQueryData<BelegListe>(listeKey, (alt) =>
      alt ? { ...alt, items: alt.items.map((b) => (b.id === id ? neu(b) : b)) } : alt,
    );
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<Beleg>(`/api/kontor/belege/${encodeURIComponent(id)}/finalize`, {}),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: listeKey });
      ersetzen(id, (b) => ({ ...b, status: "erfasst" }));
    },
    onSuccess: (neu, id) => {
      ersetzen(id, (b) => ({ ...b, ...neu }));
      finanzenNachziehenOhneBelegliste(qc);
    },
    onError: () => aktualisieren(),
  });
}

/** Pauschalrechnung eines Helfers: fehlende Projektpauschale aus dem Beleg anlegen und übernehmen. */
export function useBelegAlsPauschale() {
  const aktualisieren = useBelegeAktualisieren();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<Beleg>(`/api/kontor/belege/${encodeURIComponent(id)}/pauschale-anlegen`, {}),
    onSuccess: () => {
      aktualisieren();
      qc.invalidateQueries({ queryKey: ["projekt"] });
    },
  });
}

/**
 * Einen falsch erfassten Beleg aus dem Verkehr ziehen.
 *
 * Der Server entscheidet, was passiert: vor dem DATEV-Versand wird der Beleg
 * samt Datei und Buchungen geloescht, danach nur noch storniert (Status
 * `verworfen`, Buchungen weg, Datei bleibt als Nachweis). Deshalb kommt der
 * Hinweistext aus der Antwort und wird nicht hier zusammengebaut.
 */
export function useBelegVerwerfen() {
  const aktualisieren = useBelegeAktualisieren();
  return useMutation({
    mutationFn: (v: { belegId: string; grund?: string }) =>
      apiPost<{ modus: "geloescht" | "storniert"; hinweis: string }>(
        `/api/kontor/belege/${encodeURIComponent(v.belegId)}/verwerfen`,
        { grund: v.grund ?? "" },
      ),
    onSuccess: aktualisieren,
  });
}

export function useBelegLoeschen() {
  const aktualisieren = useBelegeAktualisieren();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/api/kontor/belege/${encodeURIComponent(id)}`),
    onSuccess: aktualisieren,
  });
}

/* ── Beleg ↔ Bankzeile ─────────────────────────────────────────────────
   Dieselbe Zuordnung wie im Konto-Reiter, nur aus Sicht des Belegs. Beide
   Wege landen serverseitig in denselben scanflow-Funktionen, deshalb kann
   man sie beliebig mischen. */

export function usePassendeBankzeilen(belegId: string | null, aktiv: boolean) {
  return useQuery<PassendeBankzeile[]>({
    queryKey: [...belegeKey, "bankzeilen", belegId],
    queryFn: () =>
      apiGet<PassendeBankzeile[]>(
        `/api/kontor/belege/${encodeURIComponent(belegId ?? "")}/bankzeilen?limit=10`,
      ),
    enabled: aktiv && !!belegId,
    staleTime: 15_000,
  });
}

export function useBankzeileZuordnen() {
  const aktualisieren = useBelegeAktualisieren();
  return useMutation({
    mutationFn: (v: { belegId: string; kontobewegungId: string }) =>
      apiPost<Beleg>(`/api/kontor/belege/${encodeURIComponent(v.belegId)}/bankzeile`, {
        kontobewegung_id: v.kontobewegungId,
      }),
    onSuccess: aktualisieren,
  });
}

export function useBankzeileLoesen() {
  const aktualisieren = useBelegeAktualisieren();
  return useMutation({
    mutationFn: (belegId: string) =>
      apiDelete<Beleg>(`/api/kontor/belege/${encodeURIComponent(belegId)}/bankzeile`),
    onSuccess: aktualisieren,
  });
}

/**
 * Beleg-Datei hochladen. Multipart geht nicht durch den JSON-Wrapper von
 * @vimaana/ui, deshalb hier ein eigener fetch — mit `credentials: "include"`,
 * damit das Session-Cookie mitgeht.
 */
export function useBelegHochladen() {
  const aktualisieren = useBelegeAktualisieren();
  return useMutation({
    mutationFn: async (v: { datei: File; ausgabeTyp?: string; projektId?: string }) => {
      const daten = new FormData();
      daten.append("datei", v.datei);
      daten.append("ausgabe_typ", v.ausgabeTyp ?? "");
      daten.append("projekt_id", v.projektId ?? "");
      const res = await fetch("/api/kontor/belege/upload", {
        method: "POST",
        credentials: "include",
        body: daten,
      });
      const text = await res.text();
      let gelesen: unknown = null;
      if (text) {
        try {
          gelesen = JSON.parse(text);
        } catch {
          gelesen = text;
        }
      }
      if (!res.ok) {
        const detail =
          gelesen && typeof gelesen === "object" && "detail" in gelesen
            ? String((gelesen as { detail: unknown }).detail)
            : `HTTP ${res.status}`;
        throw new Error(detail);
      }
      return gelesen as Beleg;
    },
    onSuccess: aktualisieren,
  });
}

/**
 * Kanonische Lieferantenliste — dieselbe Quelle wie in ScanFlow.
 *
 * Ohne sie war der Lieferant im Belegregister ein Freitextfeld: derselbe
 * Lieferant landete in zwei Schreibweisen, und der Stamm wuchs nur dort, wo
 * jemand ihn per Handy pflegte.
 */
export function useLieferanten() {
  return useQuery<Lieferant[]>({
    queryKey: ["lieferanten"],
    queryFn: () => apiGet<Lieferant[]>("/api/kontor/lieferanten"),
    staleTime: 5 * 60_000,
  });
}

/** Neuen Lieferanten aufnehmen. `force` überspringt die Fuzzy-Dedup. */
export function useLieferantAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiPost<Lieferant>("/api/kontor/lieferanten", { name, force: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lieferanten"] }),
  });
}


/* ══ Helferrechnung ↔ Projektpauschale (Migration 188) ══════════════════ */

const pauschalenKey = (id?: string) => [...belegeKey, "pauschalen", id] as const;

/** Verknüpfte und offene Pauschalen des Helfers zu dieser Rechnung. */
export function useBelegPauschalen(belegId?: string) {
  return useQuery<BelegPauschalenAntwort>({
    queryKey: pauschalenKey(belegId),
    queryFn: () => apiGet<BelegPauschalenAntwort>(`/api/kontor/belege/${encodeURIComponent(belegId as string)}/pauschalen`),
    enabled: !!belegId,
    staleTime: 15_000,
  });
}

/**
 * Nach dem Verknüpfen ändern sich Art und Projekt am Beleg — Liste neu laden —
 * und am Projekt steht „abgerechnet mit Rechnung vom …".
 */
function usePauschalenNachziehen(belegId: string) {
  const qc = useQueryClient();
  const aktualisieren = useBelegeAktualisieren();
  return (antwort: BelegPauschalenAntwort) => {
    qc.setQueryData(pauschalenKey(belegId), antwort);
    aktualisieren();
    qc.invalidateQueries({ queryKey: ["projekt"] });
  };
}

export function useBelegPauschalenSetzen(belegId: string) {
  const nachziehen = usePauschalenNachziehen(belegId);
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiPut<BelegPauschalenAntwort>(`/api/kontor/belege/${encodeURIComponent(belegId)}/pauschalen`, { pauschale_ids: ids }),
    onSuccess: nachziehen,
  });
}

export function useBelegPauschaleNeu(belegId: string) {
  const nachziehen = usePauschalenNachziehen(belegId);
  return useMutation({
    mutationFn: (body: { projekt_id: string; bezeichnung: string; betrag: number; datum: string; freigeben: boolean }) =>
      apiPost<BelegPauschalenAntwort>(`/api/kontor/belege/${encodeURIComponent(belegId)}/pauschalen/neu`, body),
    onSuccess: nachziehen,
  });
}
